import React, {
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MODAL_TYPES } from '@components/modals';
import {
  countSetDefaults,
  mapAppointmentTypeToDefaults,
  TypeDefaultsPreview,
} from '@components/ui/cards/TypeDefaultsPreview';
import { useCan } from '@features/user';
import { Chip as JotChip } from '@jotpsych/ui';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';
import { debounce } from 'lodash';

import { useTemplateNameById } from '../hooks/useTemplateNameById';
import { useAppointmentTypes } from '../store/hooks';
import { type AppointmentType } from '../types';

import AppointmentTypeModal from './AppointmentTypeModal';
import { ColorDot } from './ColorDot';

interface AppointmentTypeOption {
  encounter_type_id: string;
  label: string;
  appointmentType: AppointmentType;
}

interface AppointmentTypePickerProps {
  // Controlled mode props
  value?: AppointmentType | null;
  onChange?: (appointmentType: AppointmentType | null) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;

  // Uncontrolled mode props (legacy)
  onAppointmentTypeSelect?: (appointmentType: AppointmentType | undefined) => void;
  initialAppointmentType?: AppointmentType;

  /**
   * Fired when the user edits the currently-selected type's METADATA (pencil
   * icon) rather than picking a different type. Consumers that track explicit
   * picks (analytics, "Suggested" state) should refresh their value here
   * without treating it as a pick. Falls back to onChange /
   * onAppointmentTypeSelect when not provided. (GDN-2228)
   */
  onAppointmentTypeUpdated?: (appointmentType: AppointmentType) => void;

  // Common props
  onOpen?: () => void;
  autoFocus?: boolean;
  nonDismissable?: boolean; // New prop to control dismissability
  size?: 'small' | 'medium'; // New prop to control input size
  providerId?: string; // For admin: fetch encounter types for this provider
  showManageButton?: boolean;
  suggested?: boolean; // Current value was auto-selected (provider's most-used), not user-picked (GDN-2228)
  /**
   * Opt-in (A3, GDN-2230): show an info adornment on the selected value that
   * opens a "what this type fills in" popover. Leave OFF on surfaces that
   * already render session details next to the picker (e.g. EncounterViewV2's
   * SessionSummaryLine) — the per-option preview lines are always on.
   */
  showDefaultsPreview?: boolean;
}

// Export a type for the ref
export interface AppointmentTypePickerRef {
  focusInput: () => void;
}

const setRefValue = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (!ref) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  (ref).current = value;
};

const AppointmentTypePicker = forwardRef<AppointmentTypePickerRef, AppointmentTypePickerProps>(
  (
    {
      // Controlled props
      value,
      onChange,
      inputValue: controlledInputValue,
      onInputChange: controlledOnInputChange,
      // Uncontrolled props
      onAppointmentTypeSelect,
      initialAppointmentType,
      onAppointmentTypeUpdated,
      // Common props
      onOpen,
      autoFocus = false,
      nonDismissable = false,
      size = 'medium',
      providerId,
      showManageButton = true,
      suggested = false,
      showDefaultsPreview = false,
    },
    ref,
  ) => {
    // Detect if component is being used in controlled mode
    const isControlled = value !== undefined || onChange !== undefined;
    const [options, setOptions] = useState<AppointmentTypeOption[]>([]);
    const [open, setOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Internal state for uncontrolled mode
    const [uncontrolledSelectedAppointmentType, setUncontrolledSelectedAppointmentType] =
      useState<AppointmentTypeOption | null>(
        initialAppointmentType
          ? {
              encounter_type_id: initialAppointmentType.encounter_type_id,
              label: initialAppointmentType.encounter_type,
              appointmentType: initialAppointmentType,
            }
          : null,
      );
    const [uncontrolledInputValue, setUncontrolledInputValue] = useState(
      initialAppointmentType?.encounter_type || '',
    );

    // Use controlled values if provided, otherwise use internal state
    const effectiveValue = isControlled
      ? value
        ? {
            encounter_type_id: value.encounter_type_id,
            label: value.encounter_type,
            appointmentType: value,
          }
        : null
      : uncontrolledSelectedAppointmentType;

    const effectiveInputValue =
      controlledInputValue !== undefined ? controlledInputValue : uncontrolledInputValue;

    const [currentSearchText, setCurrentSearchText] = useState('');
    const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);
    const canManageAppointmentTypes = useCan('manage_appointment_types');
    const { open: openModal } = useModal();
    const { appointment: appointmentTracking } = useEventTracking();

    // A3 (GDN-2230): template_id -> display name map, used both for the
    // per-option preview lines and the selected-type popover.
    const templateNames = useTemplateNameById();
    const selectedDefaults = effectiveValue
      ? mapAppointmentTypeToDefaults(effectiveValue.appointmentType, {
          templateName: effectiveValue.appointmentType.template_id
            ? templateNames.get(effectiveValue.appointmentType.template_id) ?? null
            : null,
        })
      : null;

    const handleOpenDefaultsPreview = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setPreviewAnchor(event.currentTarget);
      if (!selectedDefaults) return;
      appointmentTracking.typeDefaultsPreviewOpened({
        appointment_type_id: effectiveValue?.encounter_type_id,
        has_template: selectedDefaults.templateSet,
        fields_set_count: countSetDefaults(selectedDefaults),
      });
    };

    // Use our custom hooks for API data
    const { appointmentTypes, isLoading, error } = useAppointmentTypes(
      currentSearchText,
      providerId,
    );

    const autocompleteRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleOpenAppointmentTypesManager = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
          source: 'appointment_type_picker',
          maxWidth: 'md',
          fullScreenOnMobile: true,
        });
      },
      [openModal],
    );

    // Expose the focusInput method through the ref
    useImperativeHandle(ref, () => ({
      focusInput: () => {
        const inputElement =
          inputRef.current ||
          (autocompleteRef.current?.querySelector('input') as HTMLInputElement | null);

        if (inputElement) {
          inputElement.focus();
        }
      },
    }));

    // Focus the input when autoFocus prop is true
    useEffect(() => {
      if (autoFocus) {
        setTimeout(() => {
          const inputElement =
            inputRef.current ||
            (autocompleteRef.current?.querySelector('input') as HTMLInputElement | null);

          if (inputElement) {
            inputElement.focus();
          }
        }, 100);
      }
    }, [autoFocus]);

    // Create debounced search function
    const debouncedSetSearchText = useCallback(
      debounce((searchTerm: string) => {
        setCurrentSearchText(searchTerm);
      }, 300),
      [],
    );

    // Update options when appointment types change
    useEffect(() => {
      if (!appointmentTypes) {
        setOptions([]);
        return;
      }

      const appointmentTypeOptions: AppointmentTypeOption[] = appointmentTypes.map(
        (appointmentType: AppointmentType) => ({
          encounter_type_id: appointmentType.encounter_type_id,
          label: appointmentType.encounter_type,
          appointmentType,
        }),
      );

      setOptions(appointmentTypeOptions);
    }, [appointmentTypes, currentSearchText]);

    useEffect(() => {
      if (!isControlled && initialAppointmentType) {
        setUncontrolledSelectedAppointmentType({
          encounter_type_id: initialAppointmentType.encounter_type_id,
          label: initialAppointmentType.encounter_type,
          appointmentType: initialAppointmentType,
        });
        setUncontrolledInputValue(initialAppointmentType.encounter_type);
      }
    }, [initialAppointmentType, isControlled]);

    const handleInputChange = (event: React.ChangeEvent<{}>, value: string, reason: string) => {
      // Update controlled or uncontrolled input value
      if (controlledOnInputChange) {
        controlledOnInputChange(value);
      } else {
        setUncontrolledInputValue(value);
      }

      if (reason === 'input') {
        // Cancel any pending debounced calls
        debouncedSetSearchText.cancel();

        if (value.trim() !== '') {
          // Use the debounced search
          debouncedSetSearchText(value);
        } else {
          // If the input is cleared, clear the search immediately
          setCurrentSearchText('');
        }
      }
    };

    const handleChange = (event: React.ChangeEvent<{}>, value: AppointmentTypeOption | null) => {
      if (value) {
        // Update value based on mode
        if (isControlled && onChange) {
          onChange(value.appointmentType);
        } else {
          setUncontrolledSelectedAppointmentType(value);
          if (onAppointmentTypeSelect) {
            onAppointmentTypeSelect(value.appointmentType);
          }
        }

        // Update input value
        if (controlledOnInputChange) {
          controlledOnInputChange(value.label);
        } else {
          setUncontrolledInputValue(value.label);
        }

        setCurrentSearchText('');
      } else {
        // Clear selection
        if (isControlled && onChange) {
          onChange(null);
        } else {
          setUncontrolledSelectedAppointmentType(null);
          if (onAppointmentTypeSelect) {
            onAppointmentTypeSelect(undefined);
          }
        }

        // Clear input value
        if (controlledOnInputChange) {
          controlledOnInputChange('');
        } else {
          setUncontrolledInputValue('');
        }

        setCurrentSearchText('');
      }
    };

    // Handle Enter key press to select the first option
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      // Prevent Escape key from closing when nonDismissable is true
      if (event.key === 'Escape' && nonDismissable && open) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (
        event.key === 'Enter' &&
        open &&
        options.length > 0 &&
        !event.shiftKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();

        const firstOption = options[0];
        // Update based on mode
        if (isControlled && onChange) {
          onChange(firstOption.appointmentType);
        } else {
          setUncontrolledSelectedAppointmentType(firstOption);
          if (onAppointmentTypeSelect) {
            onAppointmentTypeSelect(firstOption.appointmentType);
          }
        }

        // Update input value
        if (controlledOnInputChange) {
          controlledOnInputChange(firstOption.label);
        } else {
          setUncontrolledInputValue(firstOption.label);
        }

        setCurrentSearchText('');

        setOpen(false);
      }
    };

    const handleAppointmentTypeUpdated = (updatedAppointmentType: AppointmentType) => {
      // Update the selected option with new data
      const updatedOption: AppointmentTypeOption = {
        encounter_type_id: updatedAppointmentType.encounter_type_id,
        label: updatedAppointmentType.encounter_type,
        appointmentType: updatedAppointmentType,
      };

      // Editing the currently-selected type's metadata is NOT an explicit
      // pick: route it through the non-tracking callback so consumers don't
      // clear "Suggested" state or fire selection analytics. Falls back to
      // the pick callbacks for consumers that don't provide it. (GDN-2228)
      const isSameType =
        updatedAppointmentType.encounter_type_id === effectiveValue?.encounter_type_id;
      if (isSameType && onAppointmentTypeUpdated) {
        if (!isControlled) {
          setUncontrolledSelectedAppointmentType(updatedOption);
        }
        onAppointmentTypeUpdated(updatedAppointmentType);
      } else if (isControlled && onChange) {
        onChange(updatedAppointmentType);
      } else {
        setUncontrolledSelectedAppointmentType(updatedOption);
        if (onAppointmentTypeSelect) {
          onAppointmentTypeSelect(updatedAppointmentType);
        }
      }

      // Update input value
      if (controlledOnInputChange) {
        controlledOnInputChange(updatedAppointmentType.encounter_type);
      } else {
        setUncontrolledInputValue(updatedAppointmentType.encounter_type);
      }
    };

    return (
      <>
        <Autocomplete
          id="appointment-type-picker"
          size={size}
          open={open}
          onOpen={() => {
            setOpen(true);
            appointmentTracking.typePickerOpened({
              total_count: appointmentTypes?.length,
              has_suggestion: suggested,
            });
            if (onOpen) {
              onOpen();
            }
          }}
          onClose={(event, reason) => {
            // If nonDismissable is true, only allow closing when a selection is made
            if (nonDismissable && reason !== 'selectOption') {
              return;
            }
            setOpen(false);
            // Reset search when closing the picker to avoid stale filtered results
            if (currentSearchText) {
              setCurrentSearchText('');
              const labelToRestore = effectiveValue?.label || '';
              if (controlledOnInputChange) {
                controlledOnInputChange(labelToRestore);
              } else {
                setUncontrolledInputValue(labelToRestore);
              }
            }
          }}
          options={options}
          loading={isLoading}
          value={effectiveValue || null}
          inputValue={effectiveInputValue}
          onInputChange={handleInputChange}
          onChange={handleChange}
          getOptionLabel={option => option.label}
          ref={autocompleteRef}
          slotProps={{
            popper: {
              placement: 'bottom-start',
            },
          }}
          renderOption={(props, option, { index }) => {
            // A3 (GDN-2230): every option gets the same quiet ·-separated
            // summary line under its name — what picking this type fills in
            // (template name, duration, visit mode, POS/CPT, forms/reminders).
            const optionDefaults = mapAppointmentTypeToDefaults(option.appointmentType, {
              templateName: option.appointmentType.template_id
                ? templateNames.get(option.appointmentType.template_id) ?? null
                : null,
            });
            const hasDefaults = countSetDefaults(optionDefaults) > 0;
            return (
              <Box component="li" {...props} key={option.encounter_type_id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', flexGrow: 1, minWidth: 0, gap: 1 }}>
                    {/* Dot on the LEFT — matches the selected-value adornment */}
                    <Box sx={{ flexShrink: 0, mt: '4px' }}>
                      <ColorDot colorEnum={option.appointmentType.color} size={16} />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: '500' }}>{option.label}</Typography>
                      {hasDefaults && (
                        <TypeDefaultsPreview variant="inline" defaults={optionDefaults} />
                      )}
                    </Box>
                  </Box>

                  {index === 0 && (
                    <Chip
                      label="Enter"
                      size="small"
                      variant="outlined"
                      icon={<KeyboardReturnIcon fontSize="small" />}
                      sx={{
                        height: '20px',
                        mr: 1,
                        flexShrink: 0,
                        fontSize: '0.7rem',
                        '& .MuiChip-icon': {
                          fontSize: '0.9rem',
                          marginLeft: '4px',
                          marginRight: '-4px',
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Appointment Type"
              onKeyDown={handleKeyDown}
              error={!!error}
              helperText={error ? 'Failed to load appointment types' : undefined}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: effectiveValue ? (
                    <InputAdornment position="start" sx={{ marginLeft: '6px' }}>
                      <ColorDot colorEnum={effectiveValue.appointmentType.color} size={16} />
                      {suggested && (
                        <JotChip
                          label="Suggested"
                          color="info"
                          variant="soft"
                          size="xSmall"
                          sx={{ ml: 1 }}
                          title="Auto-selected from your most-used appointment types"
                        />
                      )}
                      {showDefaultsPreview && (
                        <IconButton
                          size="small"
                          sx={{ ml: 0.5, padding: '2px' }}
                          onClick={handleOpenDefaultsPreview}
                          aria-label="Preview what this appointment type fills in"
                          title="Preview what this appointment type fills in"
                        >
                          <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ) : null,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.slotProps.input.endAdornment}
                      {showManageButton && canManageAppointmentTypes && (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            size="small"
                            sx={{
                              fontSize: '0.875rem',
                              padding: '4px',
                              marginRight: '4px',
                            }}
                            onClick={handleOpenAppointmentTypesManager}
                            aria-label="Manage practice appointment types"
                            title="Manage practice appointment types"
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                      {effectiveValue && (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            size="small"
                            sx={{
                              fontSize: '0.875rem',
                              padding: '4px',
                              marginRight: '4px',
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              setModalOpen(true);
                            }}
                            aria-label="Edit appointment type"
                            title="Edit appointment type"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>
                  ),
                },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  ref: (node: HTMLInputElement | null) => {
                    setRefValue(params.slotProps.htmlInput.ref, node);
                    inputRef.current = node;
                  },
                },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) =>
            option?.encounter_type_id === value?.encounter_type_id
          }
          filterOptions={x => x}
          disableCloseOnSelect={nonDismissable}
          blurOnSelect={!nonDismissable}
          clearOnBlur={!nonDismissable}
          disableClearable={!nonDismissable}
        />

        <AppointmentTypeModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          onSave={handleAppointmentTypeUpdated}
          type="edit"
          appointmentType={effectiveValue?.appointmentType || null}
          existingAppointmentTypes={appointmentTypes}
        />

        {/* A3 (GDN-2230): defaults-preview popover for the selected type */}
        <Popover
          open={showDefaultsPreview && Boolean(previewAnchor)}
          anchorEl={previewAnchor}
          onClose={() => setPreviewAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {selectedDefaults && (
            <Box sx={{ p: 2, maxWidth: 380 }}>
              <Typography variant="subtitle2">{effectiveValue?.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Picking this appointment type fills in:
              </Typography>
              <TypeDefaultsPreview defaults={selectedDefaults} />
            </Box>
          )}
        </Popover>
      </>
    );
  },
);

export default AppointmentTypePicker;
